/**
 * /stay · the Stay pillar surface (hotels via Amadeus Self-Service).
 *
 * Visual register: matches /escape exactly · Fraunces serif headlines,
 * brass eyebrow, paper background, the same hero search bar + result
 * card pattern. This is the 4th member-facing surface alongside
 * /table /escape /enjoy.
 *
 * Embed mode: `?embed=widget` hides chrome via the existing
 * /app/frontend/src/styles/embed.css rules (just add the new
 * surface-specific testids to the hide-list). The brief modal posts
 * to /api/stay/request — same Aurelion dispatcher path that routes
 * tenant=aurelion|mindescapes submissions to MindEscapes Zoho.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import { TableFooter } from "@/components/table/TableFooter";
import { PlacesAutocomplete } from "@/components/shared/PlacesAutocomplete";
import ConciergeHero from "@/components/global/ConciergeHero";
import ConciergeOnDutyPill from "@/components/global/ConciergeOnDutyPill";
import ConciergeSurfaceMenu from "@/components/global/ConciergeSurfaceMenu";
import CrossSurfaceNav from "@/components/global/CrossSurfaceNav";
import TopCitiesGrid, { PICKS_STAY } from "@/components/global/TopCitiesGrid";
import StaticMapBand from "@/components/shared/StaticMapBand";
import PillarCard from "@/components/shared/PillarCard";
import { DrawerVoiceLine, DrawerReviewsBand } from "@/components/shared/DrawerAtmosphere";
import ReservePill from "@/components/shared/ReservePill";

import MemberContextBand from "../components/MemberContextBand";
const API = process.env.REACT_APP_BACKEND_URL || "";

// Editorial tokens (synced with /escape and /enjoy)
const T = {
  bg:     "#F4EFE6",
  card:   "#FCFAF4",
  ink:    "#211C15",
  ink2:   "#5C5447",
  ink3:   "#8A8071",
  line:   "#E5DCCB",
  line2:  "#D8CDB7",
  brass:  "#8B6914",
};

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function StayPage() {
  const [q, setQ]               = useState("");
  const [checkIn, setCheckIn]   = useState(todayPlus(14));
  const [checkOut, setCheckOut] = useState(todayPlus(17));
  const [adults, setAdults]     = useState(2);
  const [results, setResults]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]       = useState(null);
  const [briefHotel, setBriefHotel] = useState(null);  // brief modal · null | hotelCard | {open_ask:true}
  const [openDetail, setOpenDetail] = useState(null);  // drawer · null | hotelCard
  // Smart filters applied client-side on the loaded cards. Cheap +
  // instant · no extra Google calls. Defaults keep everything visible.
  const [filterPrice, setFilterPrice] = useState("any");     // any · "$" · "$$" · "$$$" · "$$$$"
  const [filterMinRating, setFilterMinRating] = useState(0); // 0 · 3 · 4 · 4.5
  const [filterType, setFilterType]   = useState("any");     // any · hotel · resort_hotel · bed_and_breakfast · hostel
  const [sortBy, setSortBy]           = useState("rating");  // rating · popularity · price_asc · price_desc · name

  // Viewport-aware search-bar layout · prevents "Find stays" button
  // from being cut off on phones/iframes <760px (founder bug Feb 28).
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 760px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  async function runSearch(query) {
    const term = (query ?? q).trim();
    if (!term) return;
    setLoading(true);
    setError(null);
    // Clear previous results immediately · the user types a new
    // city and SHOULD NOT see the old city's cards through the
    // network gap. The filter bar / load-more disappear with the
    // results until the new payload lands.
    setResults(null);
    // Reset filters on a fresh search · they're search-scoped.
    setFilterPrice("any");
    setFilterMinRating(0);
    setFilterType("any");
    setSortBy("rating");
    try {
      const url = new URL(`${API}/api/stay/search`);
      url.searchParams.set("q", term);
      if (checkIn)  url.searchParams.set("check_in", checkIn);
      if (checkOut) url.searchParams.set("check_out", checkOut);
      url.searchParams.set("adults", String(adults));
      const r = await fetch(url.toString());
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `search_failed (${r.status})`);
      setResults(data);
    } catch (e) {
      setError(e.message || "Could not load stays.");
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!results?.next_page_token || loadingMore) return;
    setLoadingMore(true);
    try {
      const url = new URL(`${API}/api/stay/search`);
      url.searchParams.set("q", results.city_query);
      if (checkIn)  url.searchParams.set("check_in", checkIn);
      if (checkOut) url.searchParams.set("check_out", checkOut);
      url.searchParams.set("adults", String(adults));
      url.searchParams.set("page_token", results.next_page_token);
      const r = await fetch(url.toString());
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `loadmore_failed (${r.status})`);
      setResults((prev) => ({
        ...prev,
        cards: [...(prev.cards || []), ...(data.cards || [])],
        next_page_token: data.next_page_token || null,
      }));
    } catch (e) {
      setError(e.message || "Could not load more stays.");
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div data-testid="stay-page-root" data-table-surface="stay" style={{ background: T.bg, minHeight: "100vh", color: T.ink, fontFamily: "'Hanken Grotesk', system-ui, sans-serif", "--t-bg": T.bg, "--t-ink": T.ink, "--t-ink2": T.ink2, "--t-ink3": T.ink3, "--t-line": T.line, "--t-brass": T.brass, "--t-verified": "#2f8a52" }}>
      <StayTopBar onHome={() => { setResults(null); setQ(""); }} />
      <MemberContextBand pillar="stay" />

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "12px 28px 80px" }}>
{/* Hero — matches /escape's centred Concierge® hero atom */}
        <ConciergeHero
          testid="stay-hero"
          eyebrow="Concierge® · Stay"
          headline="Where would you like to"
          headlineTail="stay?"
          subtitle={<>Hotels, resorts and private residences&mdash;with the Concierge® beside you.</>}
          maxWidth={980}
        >
          {/* Search bar */}
          <form
            data-testid="stay-search-bar"
            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            style={{
              background: T.card,
              border: `1px solid ${T.line2}`,
              borderRadius: 18,
              padding: isNarrow ? "12px" : "8px 8px 8px 18px",
              display: "grid",
              gridTemplateColumns: isNarrow
                ? "1fr 1fr"
                : "1.4fr 1fr 1fr 0.7fr auto",
              gap: isNarrow ? 10 : 8,
              alignItems: "center",
              maxWidth: 980,
              margin: "0 auto",
              boxShadow: "0 6px 22px rgba(33,28,21,0.04)",
            }}
          >
            <div
              style={{
                gridColumn: isNarrow ? "1 / -1" : "auto",
                position: "relative",
                minWidth: 0,
              }}
            >
              <PlacesAutocomplete
                value={q}
                onChange={setQ}
                onSubmit={(picked) => { setQ(picked); runSearch(picked); /* v3.48h · fire search on pick */ }}
                placeholder="Search a city or a property — Paris · The Imperial · Goa…"
                types="lodging"
                testidPrefix="stay-search"
                inputStyle={{
                  border: 0, background: "transparent", outline: "none",
                  borderRadius: 0, fontSize: 16, color: T.ink,
                  padding: "10px 12px",
                  fontFamily: "inherit",
                }}
              />
            </div>
            <DateField label="Check-in"  value={checkIn}  onChange={setCheckIn}  testid="stay-checkin" />
            <DateField label="Check-out" value={checkOut} onChange={setCheckOut} testid="stay-checkout" />
            <StepperField label="Guests" value={adults} onChange={setAdults} min={1} max={9} testid="stay-adults" />
            <button
              type="submit"
              data-testid="stay-search-submit"
              disabled={loading || !q.trim()}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                background: T.ink, color: T.card, border: 0, borderRadius: 14,
                padding: "12px 22px", fontSize: 14, fontWeight: 600,
                cursor: loading ? "wait" : (!q.trim() ? "not-allowed" : "pointer"),
                opacity: (loading || !q.trim()) ? 0.65 : 1,
                fontFamily: "inherit",
                touchAction: "manipulation",
                gridColumn: isNarrow ? "1 / -1" : "auto",
                width: isNarrow ? "100%" : "auto",
              }}
            >
              {loading ? "Searching…" : "Find stays"}
            </button>
          </form>

          {/* Two CTA row · matches /escape ("Browse escapes" + "Ask the Concierge®") */}
          <div
            data-testid="stay-hero-secondary-row"
            style={{
              display: "flex", justifyContent: "center", alignItems: "center",
              gap: 18, marginTop: 18, flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              data-testid="stay-browse-stays"
              onClick={() => runSearch("Paris")}
              style={{
                background: "transparent",
                color: T.ink,
                border: `1.5px solid ${T.ink}`,
                borderRadius: 999,
                padding: "9px 18px",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Browse stays
            </button>
            <button
              type="button"
              data-testid="stay-hero-ask-concierge"
              onClick={() => setBriefHotel({ _open_ask: true })}
              style={{
                background: "transparent",
                color: T.brass,
                border: `1px solid ${T.brass}`,
                borderRadius: 999,
                padding: "10px 22px",
                fontSize: 13.5,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
            >
              Ask the Concierge®
            </button>
          </div>
          <p
            style={{
              fontSize: 12.5,
              color: T.ink3,
              fontStyle: "italic",
              marginTop: 14,
              marginBottom: 0,
            }}
          >
            Book directly or ask your Concierge® to arrange it.
          </p>
        </ConciergeHero>

        {/* Top destinations grid · shown only on the empty/landing
            state · vanishes the moment a search runs (or the user is
            inside the iframe embed mode). */}
        {!results && !loading && !error && (
          <TopCitiesGrid
            pillar="stay"
            picks={PICKS_STAY}
            onPick={(query) => { setQ(query); runSearch(query); }}
            eyebrow="Main cities"
            title="Cities we book often"
          />
        )}

        {/* Results */}
        {error && (
          <p data-testid="stay-error" style={{ textAlign: "center", color: "#9a3535", fontSize: 14 }}>{error}</p>
        )}
        {results && results.cards?.length === 0 && (
          <div data-testid="stay-empty" style={{ textAlign: "center", padding: "48px 20px", color: T.ink2, maxWidth: 620, margin: "0 auto" }}>
            {results.non_lodging_hit ? (
              <>
                <p style={{ fontSize: 15, marginBottom: 8, lineHeight: 1.55 }}>
                  We found <strong style={{ color: T.ink }}>{results.non_lodging_hit.name}</strong>
                  {results.non_lodging_hit.short_address ? <> in {results.non_lodging_hit.short_address}</> : null}
                  {" — "}but our index resolves to a{" "}
                  <strong style={{ color: T.ink }}>
                    {(results.non_lodging_hit.types || []).find((t) => ["restaurant", "cafe", "bar", "tourist_attraction"].includes(t))?.replace("_", " ") || "venue"}
                  </strong>, not as lodging.
                </p>
                <p style={{ fontSize: 13.5, color: T.ink3, marginBottom: 22, lineHeight: 1.55, fontStyle: "italic" }}>
                  /stay surfaces hotels, resorts and private residences only · the Concierge® team can still arrange a stay there if it offers one.
                </p>
                <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                  {results.non_lodging_hit.pivot_pillar === "table" && (
                    <a
                      href={`/table?q=${encodeURIComponent(results.non_lodging_hit.name)}`}
                      data-testid="stay-empty-pivot-table"
                      style={{
                        background: "transparent", color: T.ink,
                        border: `1.5px solid ${T.ink}`, borderRadius: 999,
                        padding: "11px 22px", fontSize: 13.5, fontWeight: 500,
                        textDecoration: "none", fontFamily: "inherit",
                      }}
                    >
                      Try /table for this →
                    </a>
                  )}
                  {results.non_lodging_hit.pivot_pillar === "enjoy" && (
                    <a
                      href={`/enjoy?q=${encodeURIComponent(results.non_lodging_hit.name)}`}
                      data-testid="stay-empty-pivot-enjoy"
                      style={{
                        background: "transparent", color: T.ink,
                        border: `1.5px solid ${T.ink}`, borderRadius: 999,
                        padding: "11px 22px", fontSize: 13.5, fontWeight: 500,
                        textDecoration: "none", fontFamily: "inherit",
                      }}
                    >
                      Try /enjoy for this →
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => setBriefHotel({ _open_ask: true, hint_city: results.city_query })}
                    data-testid="stay-empty-ask-concierge"
                    style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "11px 22px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                  >
                    Ask the Concierge® anyway
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 15, marginBottom: 18 }}>
                  No live lodging found for <strong style={{ color: T.ink }}>{results.city_query}</strong>. The Concierge® will find it by hand.
                </p>
                <button
                  type="button"
                  onClick={() => setBriefHotel({ _open_ask: true, hint_city: results.city_query })}
                  style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "12px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Send a brief
                </button>
              </>
            )}
          </div>
        )}
        {results && results.cards?.length > 0 && (
          <>
            {results.branded_venue_hint && (
              <BrandedVenueBanner
                hint={results.branded_venue_hint}
                onRequest={() => setBriefHotel({
                  _open_ask: true,
                  hint_city: results.city_query,
                  hint_property: results.branded_venue_hint.name,
                })}
              />
            )}
            <StayFilterBar
              cards={results.cards}
              filterPrice={filterPrice}      setFilterPrice={setFilterPrice}
              filterMinRating={filterMinRating} setFilterMinRating={setFilterMinRating}
              filterType={filterType}        setFilterType={setFilterType}
              sortBy={sortBy}                setSortBy={setSortBy}
            />
            <FilteredResultsGrid
              cards={results.cards}
              filterPrice={filterPrice}
              filterMinRating={filterMinRating}
              filterType={filterType}
              sortBy={sortBy}
              onOpen={setOpenDetail}
              onBrief={(h) => setBriefHotel(h)}
            />
            {results.next_page_token && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 36 }}>
                <button
                  type="button"
                  data-testid="stay-load-more"
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{
                    background: "transparent", color: T.ink,
                    border: `1.5px solid ${T.ink}`, borderRadius: 999,
                    padding: "12px 28px", fontSize: 13.5, fontWeight: 500,
                    cursor: loadingMore ? "wait" : "pointer",
                    fontFamily: "inherit",
                    opacity: loadingMore ? 0.6 : 1,
                  }}
                >
                  {loadingMore ? "Loading…" : `Load more stays · ${results.cards.length} shown`}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <TableFooter slug="stay" />

      {briefHotel && (
        <StayRequestModal
          hotel={briefHotel}
          city={results?.city_query || briefHotel.hint_city || ""}
          checkIn={checkIn}
          checkOut={checkOut}
          adults={adults}
          onClose={() => setBriefHotel(null)}
        />
      )}

      {openDetail && (
        <StayDetailDrawer
          card={openDetail}
          checkIn={checkIn}
          checkOut={checkOut}
          adults={adults}
          onClose={() => setOpenDetail(null)}
          onRequest={() => { setBriefHotel(openDetail); setOpenDetail(null); }}
        />
      )}
    </div>
  );
}

// ── Hotel card · clickable thumbnail · /table discipline · the
//    full atmosphere (photos, rates, booking) lives in the drawer.
function HotelCard({ hotel, onOpen }) {
  const hasPhoto = !!hotel.photo_url;
  const rating = hotel.rating ? Number(hotel.rating) : null;
  const ratingCount = hotel.user_ratings_total;
  const priceTier = _priceTierLabel(hotel.price_level);
  const reserveHref = hotel.google_maps_url || hotel.maps_url || hotel.website || null;
  return (
    <article
      data-testid="stay-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
      style={{
        background: T.card, border: `1px solid ${T.line}`, borderRadius: 16,
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(33,28,21,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)";  e.currentTarget.style.boxShadow = "none"; }}
    >
      <div
        data-testid="stay-card-photo"
        style={{
          aspectRatio: "4 / 3",
          position: "relative",
          background: hasPhoto
            ? `linear-gradient(180deg, rgba(33,28,21,0) 55%, rgba(33,28,21,0.18) 100%), url(${hotel.photo_url}) center/cover no-repeat`
            : `linear-gradient(135deg, ${T.line} 0%, ${T.line2} 60%, ${T.brass}33 100%)`,
          position: "relative",
        }}
      >
        {!hasPhoto && (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', 'EB Garamond', serif",
            fontSize: 30, fontWeight: 500, color: T.brass,
            letterSpacing: "-0.01em",
          }}>
            {(hotel.name || "S").trim().charAt(0).toUpperCase()}
          </div>
        )}
        {priceTier && (
          <span
            data-testid="stay-card-price-tier"
            style={{
              position: "absolute", top: 12, left: 12,
              background: "rgba(33,28,21,0.78)", color: T.bg,
              padding: "3px 9px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.16em",
              fontWeight: 600,
            }}
          >
            {priceTier}
          </span>
        )}
        {/* Reserve pill · front-of-card primary action across all
           pillars (founder ruling 30 Jun 2026 evening). Site + phone
           live inside the drawer; the card carries the quick reserve. */}
        <ReservePill href={reserveHref} testid="stay-card-reserve" T={T} intentMeta={{ benefit: "stay", venue: v }} />
      </div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        <h3 className="t-serif" style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.22, margin: 0, color: T.ink }}>
          {hotel.name || "Untitled stay"}
        </h3>
        <p style={{ fontSize: 11.5, color: T.ink3, margin: 0, lineHeight: 1.4 }}>
          {hotel.short_address || hotel.address_line || hotel.city_name || "—"}
        </p>
        {rating && (
          <p
            data-testid="stay-card-rating"
            style={{ fontSize: 12, color: T.ink2, margin: 0, display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}
          >
            <span style={{ color: T.brass, fontWeight: 700 }}>★</span>
            <span style={{ fontWeight: 600, color: T.ink }}>{rating.toFixed(1)}</span>
            {ratingCount ? (
              <span style={{ color: T.ink3 }}>
                ({ratingCount.toLocaleString()})
              </span>
            ) : null}
          </p>
        )}
        {/* Live nightly rate · hotels engine · display-only (the desk
            books). Rule 14: cites the underlying rate source. */}
        {hotel.price_per_night != null && (
          <p
            data-testid="stay-card-live-rate"
            style={{ fontSize: 12.5, margin: 0, marginTop: 2, color: T.ink }}
          >
            <span style={{ fontWeight: 700 }}>
              from {new Intl.NumberFormat(undefined, { style: "currency", currency: hotel.price_currency || "USD", maximumFractionDigits: 0 }).format(hotel.price_per_night)}
            </span>
            <span style={{ color: T.ink3 }}> / night{hotel.price_source ? ` · ${hotel.price_source}` : ""}</span>
          </p>
        )}
      </div>
    </article>
  );
}

function _priceTierLabel(level) {
  if (!level) return null;
  // Google: PRICE_LEVEL_FREE / INEXPENSIVE / MODERATE / EXPENSIVE / VERY_EXPENSIVE
  const map = {
    PRICE_LEVEL_INEXPENSIVE:   "$",
    PRICE_LEVEL_MODERATE:      "$$",
    PRICE_LEVEL_EXPENSIVE:     "$$$",
    PRICE_LEVEL_VERY_EXPENSIVE: "$$$$",
  };
  return map[level] || null;
}


// ── Brief modal · mirrors EscapeRequestModal / EnjoyRequestModal ──
function StayRequestModal({ hotel, city, checkIn, checkOut, adults, onClose }) {
  const isOpenAsk = !!hotel?._open_ask;
  const initialHotelName = isOpenAsk ? "" : (hotel.name || "");
  const initialCity = isOpenAsk ? (hotel.hint_city || city || "") : (hotel.city_name || city || "");

  const embedMember = useEmbedMember();
  const [hotelName, setHotelName] = useState(initialHotelName);
  const [cityVal, setCityVal]     = useState(initialCity);
  const [name, setName]           = useState(embedMember?.name  || "");
  const [email, setEmail]         = useState(embedMember?.email || "");
  const [phone, setPhone]         = useState(embedMember?.phone || "");
  const [partyAdults, setPartyAdults] = useState(adults || 2);
  const [children, setChildren]   = useState(0);
  const [arrivalIn, setArrivalIn] = useState(checkIn || "");
  const [arrivalOut, setArrivalOut] = useState(checkOut || "");
  const [occasion, setOccasion]   = useState("");
  const [preferences, setPreferences] = useState("");
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState(null);
  const [done, setDone]           = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (isOpenAsk && !hotelName.trim() && !cityVal.trim()) {
      setError("Tell us a city — or the property you have in mind.");
      return;
    }
    if (!name.trim() || (!email.trim() && !phone.trim())) {
      setError("Your name plus email or phone — so the Concierge® can reply.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/stay/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hotel_name: hotelName.trim() || hotel.name || null,
          hotel_id:   hotel.hotel_id || null,
          city:       cityVal.trim() || null,
          country:    hotel.country_code || null,
          check_in:   arrivalIn || null,
          check_out:  arrivalOut || null,
          adults:     Number(partyAdults) || 2,
          children:   Number(children) || 0,
          preferences: preferences.trim() || null,
          occasion:    occasion.trim() || null,
          member_name:  name.trim(),
          member_email: email.trim() || undefined,
          member_phone: phone.trim() || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `submit_failed (${r.status})`);
      setDone(data);
    } catch (e) {
      setError(e.message || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Backdrop onClose={onClose}>
        <div data-testid="stay-request-success" style={modalStyle}>
          <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · STAY</div>
          <h2 className="t-serif" style={{ fontSize: 26, fontWeight: 500, margin: "10px 0 14px", color: T.ink }}>Your brief is with the Concierge®.</h2>
          <p style={{ fontSize: 14.5, color: T.ink2, lineHeight: 1.55 }}>
            We&apos;ll be in touch on <strong style={{ color: T.ink }}>{email || phone}</strong> very shortly with the next step.
          </p>
          <div data-testid="stay-request-ref" style={{ display: "inline-block", padding: "9px 16px", background: T.card, border: `1px solid ${T.brass}`, borderRadius: 999, fontFamily: "ui-monospace, monospace", fontSize: 12, color: T.brass, fontWeight: 600, marginTop: 16 }}>
            {done.display_ref || done.member_reference || "REF-PENDING"}
          </div>
          <div style={{ marginTop: 22 }}>
            <button type="button" onClick={onClose} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
          </div>
        </div>
      </Backdrop>
    );
  }

  return (
    <Backdrop onClose={onClose}>
      <div data-testid="stay-request-modal" style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · STAY</div>
        <h2 className="t-serif" style={{ fontSize: 24, fontWeight: 500, margin: "10px 0 8px", color: T.ink }}>
          {isOpenAsk ? "Your stay of choice" : "Request via Concierge®"}
        </h2>
        <p style={{ fontSize: 13.5, color: T.ink2, margin: "0 0 18px" }}>
          {isOpenAsk
            ? "Tell us where — we'll match the right property."
            : (hotel.name || "")}
        </p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {isOpenAsk && (
            <>
              <Input value={hotelName} onChange={setHotelName} placeholder="Property of choice (optional)" testid="stay-modal-hotel" />
              <Input value={cityVal} onChange={setCityVal} placeholder="City (helps a lot)" testid="stay-modal-city" autoFocus />
            </>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={arrivalIn} onChange={setArrivalIn} placeholder="Check-in (YYYY-MM-DD)" testid="stay-modal-checkin" type="date" />
            <Input value={arrivalOut} onChange={setArrivalOut} placeholder="Check-out (YYYY-MM-DD)" testid="stay-modal-checkout" type="date" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={partyAdults} onChange={setPartyAdults} placeholder="Adults" testid="stay-modal-adults" type="number" />
            <Input value={children} onChange={setChildren} placeholder="Children" testid="stay-modal-children" type="number" />
          </div>
          <Input value={name} onChange={setName} placeholder="Your name" testid="stay-modal-name" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={email} onChange={setEmail} placeholder="Email" testid="stay-modal-email" type="email" />
            <Input value={phone} onChange={setPhone} placeholder="Phone / WhatsApp" testid="stay-modal-phone" />
          </div>
          <Input value={occasion} onChange={setOccasion} placeholder="Occasion (optional · honeymoon, work, etc.)" testid="stay-modal-occasion" />
          <textarea
            data-testid="stay-modal-preferences"
            value={preferences}
            onChange={(e) => setPreferences(e.target.value)}
            placeholder="Anything else? Comfort band, must-haves, preferred location…"
            rows={3}
            style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
          />
          {error && <p data-testid="stay-modal-error" style={{ color: "#9a3535", fontSize: 13, margin: 0 }}>{error}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
            <button type="button" onClick={onClose} style={{ background: "transparent", color: T.ink2, border: `1px solid ${T.line2}`, borderRadius: 999, padding: "10px 18px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button type="submit" data-testid="stay-modal-submit" disabled={busy} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.65 : 1, fontFamily: "inherit" }}>
              {busy ? "Sending…" : "Send to Concierge® →"}
            </button>
          </div>
        </form>
      </div>
    </Backdrop>
  );
}

// ── Primitives ───────────────────────────────────────────────────
function Backdrop({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(33,28,21,0.55)", zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      {children}
    </div>
  );
}

const modalStyle = {
  background: T.bg, color: T.ink, borderRadius: 18, padding: "28px 28px 22px",
  width: "min(540px, 100%)", maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
};

const inputStyle = {
  width: "100%", padding: "11px 14px", border: `1px solid ${T.line2}`,
  borderRadius: 12, background: T.card, color: T.ink, fontSize: 14.5,
  fontFamily: "inherit", outline: "none", boxSizing: "border-box",
};

function Input({ value, onChange, placeholder, testid, type = "text", autoFocus }) {
  return (
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      style={inputStyle}
    />
  );
}

function DateField({ label, value, onChange, testid }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", padding: "4px 6px", borderLeft: `1px solid ${T.line}` }}>
      <span style={{ fontSize: 10, letterSpacing: "0.14em", color: T.ink3, textTransform: "uppercase", marginBottom: 2 }}>{label}</span>
      <input
        type="date"
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ border: 0, background: "transparent", outline: "none", fontSize: 14, color: T.ink, padding: 0, fontFamily: "inherit" }}
      />
    </label>
  );
}

function StepperField({ label, value, onChange, min, max, testid }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "4px 6px", borderLeft: `1px solid ${T.line}` }}>
      <span style={{ fontSize: 10, letterSpacing: "0.14em", color: T.ink3, textTransform: "uppercase", marginBottom: 2 }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} style={stepBtnStyle} aria-label="Decrease">−</button>
        <span data-testid={testid} style={{ fontSize: 14, fontWeight: 600, minWidth: 18, textAlign: "center", color: T.ink }}>{value}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))} style={stepBtnStyle} aria-label="Increase">+</button>
      </div>
    </div>
  );
}

const stepBtnStyle = {
  background: "transparent", border: `1px solid ${T.line2}`, borderRadius: 6,
  width: 22, height: 22, fontSize: 14, lineHeight: 1, color: T.ink2,
  cursor: "pointer", fontFamily: "inherit",
};


/* ── StayTopBar · mirrors /escape's TopBar exactly (sticky, paper bg,
 *    serif wordmark, "by Concierge®", green Concierge® on duty pill,
 *    CrossSurfaceNav, mobile menu). The currency dropdown that /escape
 *    carries is omitted here · /stay surfaces per-hotel currency from
 *    Amadeus and never needs a global selector. */
function StayTopBar({ onHome }) {
  return (
    <header
      data-testid="stay-header"
      style={{
        borderBottom: `1px solid ${T.line}`,
        background: T.bg,
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
    >
      <div
        style={{
          maxWidth: 1180, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 22px", gap: 10, flexWrap: "nowrap", minWidth: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
          <Link
            to="/"
            onClick={(e) => { if (onHome) { e.preventDefault(); onHome(); } }}
            data-testid="stay-home-button"
            style={{ textDecoration: "none", color: T.ink, display: "inline-flex", alignItems: "baseline", gap: 10, minWidth: 0 }}
          >
            <span className="t-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "0.02em" }}>
              /stay
            </span>
            <span
              data-testid="stay-header-by-concierge"
              className="lc-mobile-hide-on-narrow"
              style={{
                fontFamily: "ui-monospace, monospace",
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: T.ink3,
                whiteSpace: "nowrap",
              }}
            >
              by Concierge®
            </span>
          </Link>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <ConciergeOnDutyPill context="stay" />
          <CrossSurfaceNav testidPrefix="stay-header-nav" size="compact" />
          <ConciergeSurfaceMenu testid="stay-surface-menu" />
        </div>
      </div>
    </header>
  );
}


/* ── StayDetailDrawer · the full atmosphere drawer · clones /table's
 *    Reservation Routing chrome. Fetches /api/stay/place/{id} for
 *    rich data (gallery, description, hours, phone, website, price
 *    tier). Two CTAs:
 *      · "Continue to booking ↗" → Google Travel hotel deeplink
 *        (member sees live rates from every OTA + Reserve button)
 *      · "Request via Concierge®" → opens our brief modal · the
 *        desk does the booking. */
function StayDetailDrawer({ card, checkIn, checkOut, adults, onClose, onRequest }) {
  const [data, setData] = useState(card);    // start with what the search card knows
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  // Tables-nearby · cross-pillar Concierge® moment (29 Jun 2026).
  // Loads after the detail call so the drawer first paint stays
  // snappy; the strip slides in as data arrives.
  const [tables, setTables] = useState(null);
  const [tablesLoading, setTablesLoading] = useState(true);
  // Concierge® voice line · Parity Sprint (30 Jun 2026)
  const [voiceLine, setVoiceLine] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!card?.hotel_id) { setLoadingDetail(false); return; }
      try {
        const params = new URLSearchParams();
        if (checkIn)  params.set("check_in", checkIn);
        if (checkOut) params.set("check_out", checkOut);
        if (adults)   params.set("adults", String(adults));
        const r = await fetch(`${API}/api/stay/place/${encodeURIComponent(card.hotel_id)}?${params}`);
        if (!r.ok) throw new Error(`detail_${r.status}`);
        const j = await r.json();
        if (cancelled) return;
        const merged = { ...card, ...(j.hotel || {}) };
        setData(merged);
        // G4 · save the most recent hotel to localStorage so /airport
        // can render an ETA whisper when the member lands there next.
        try {
          const lat = merged.lat ?? merged?.geometry?.location?.lat;
          const lng = merged.lng ?? merged?.geometry?.location?.lng;
          if (lat != null && lng != null) {
            window.localStorage?.setItem("lc_recent_hotel", JSON.stringify({
              id:        merged.hotel_id || card.hotel_id,
              name:      merged.name || card.name || "",
              city:      merged.city_name || card.city_name || "",
              lat, lng,
              saved_at:  new Date().toISOString(),
            }));
          }
        } catch (_e) { /* localStorage may be blocked; silent fail */ }
      } catch {
        if (!cancelled) setData(card);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [card?.hotel_id]);

  // Tables-nearby · soft-fail, no spinner-on-spinner. We fire this
  // once the hotel_id is known; the strip only renders if results
  // come back.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!card?.hotel_id) { setTablesLoading(false); return; }
      try {
        const r = await fetch(
          `${API}/api/stay/place/${encodeURIComponent(card.hotel_id)}/tables-nearby?limit=8`,
        );
        if (!r.ok) throw new Error("tables_nearby_failed");
        const j = await r.json();
        if (!cancelled) setTables(j.results || []);
      } catch {
        if (!cancelled) setTables([]);
      } finally {
        if (!cancelled) setTablesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [card?.hotel_id]);

  // Concierge® voice line · Parity Sprint
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pid = data?.hotel_id || card?.hotel_id;
      if (!pid || !data?.name) return;
      try {
        const params = new URLSearchParams({ name: data.name });
        const rawSummary = data.editorial_summary;
        const summary = (typeof rawSummary === "string") ? rawSummary
          : (rawSummary && typeof rawSummary === "object")
            ? (rawSummary.overview || rawSummary.text || "")
            : "";
        if (summary) params.set("editorial", summary.slice(0, 1500));
        if (data.neighborhood) params.set("neighborhood", data.neighborhood);
        if (data.city_name || data.city) params.set("city", data.city_name || data.city);
        if (data.primary_type_label) params.set("types_label", data.primary_type_label);
        const r = await fetch(`${API}/api/concierge/voice/stay/${encodeURIComponent(pid)}?${params.toString()}`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.voice_line) setVoiceLine(j.voice_line);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [data?.hotel_id, card?.hotel_id, data?.name, data?.editorial_summary, data?.neighborhood, data?.city_name, data?.city, data?.primary_type_label]);

  const photos = (data.photos && data.photos.length ? data.photos : [data.photo_url].filter(Boolean));
  const hero = photos[activePhoto] || photos[0];
  const rating = data.rating ? Number(data.rating) : null;
  const ratingCount = data.user_ratings_total;
  const priceTier = _priceTierLabel(data.price_level);
  const openNow = data.open_now;
  const isHotel = (data.place_types || []).some((t) => t === "lodging" || t === "hotel");

  function copyShareLink() {
    const url = `${API}/stay?q=${encodeURIComponent(data.name || "")}`;
    try {
      navigator.clipboard?.writeText(url);
    } catch { /* user denied · ignore */ }
  }

  return (
    <div
      data-testid="stay-drawer-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20,16,11,0.55)",
        display: "flex", justifyContent: "flex-end",
        zIndex: 1000,
      }}
    >
      <aside
        data-testid="stay-drawer"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 96vw)",
          background: T.card,
          color: T.ink,
          height: "100%",
          overflowY: "auto",
          boxShadow: "-12px 0 36px rgba(0,0,0,0.22)",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Chrome · sticky · RESERVATION ROUTING + Share + Save + close */}
        <div
          data-testid="stay-drawer-chrome"
          style={{
            position: "sticky", top: 0, zIndex: 2,
            background: T.card,
            borderBottom: `1px solid ${T.line}`,
            padding: "14px 22px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          }}
        >
          <span
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.22em",
              color: T.brass, fontWeight: 600, textTransform: "uppercase",
            }}
          >
            Reservation Routing
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              data-testid="stay-drawer-share"
              onClick={copyShareLink}
              style={chromeBtnStyle}
            >Share ↗</button>
            <button
              type="button"
              data-testid="stay-drawer-print"
              onClick={() => window.print()}
              style={chromeBtnStyle}
            >↓ Save as card</button>
            <button
              type="button"
              data-testid="stay-drawer-close"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: "transparent", border: 0,
                fontSize: 20, cursor: "pointer", color: T.ink2,
                padding: "0 8px",
              }}
            >×</button>
          </div>
        </div>

        {/* Hero photo */}
        {hero && (
          <div
            data-testid="stay-drawer-hero"
            style={{
              aspectRatio: "16 / 10",
              background: `url(${hero}) center/cover no-repeat`,
            }}
          />
        )}

        <div style={{ padding: "22px 26px 30px", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Branded ribbon · "Through your Concierge® · Concierge® · Est 1998" */}
          <div
            data-testid="stay-drawer-ribbon"
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              fontFamily: "ui-monospace, monospace",
              fontSize: 10.5, letterSpacing: "0.20em",
              color: T.brass, fontWeight: 600, textTransform: "uppercase",
              borderBottom: `1px solid ${T.line}`,
              paddingBottom: 12,
            }}
          >
            <span>✦ Through your Concierge®</span>
            <span>Concierge® · Est 1998</span>
          </div>

          {/* Title */}
          <h2
            className="t-serif"
            data-testid="stay-drawer-title"
            style={{
              fontSize: 30, lineHeight: 1.1, fontWeight: 500,
              margin: 0, color: T.ink,
            }}
          >
            {data.name || "—"}
          </h2>

          {/* Status line · location · ★ rating · open now · price tier · TYPE */}
          <div
            data-testid="stay-drawer-status"
            style={{
              fontSize: 13, color: T.ink2,
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}
          >
            {data.primary_type_label && (
              <span
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 10.5, letterSpacing: "0.16em",
                  color: T.brass, fontWeight: 600, textTransform: "uppercase",
                  border: `1px solid ${T.brass}55`,
                  padding: "2px 9px", borderRadius: 999,
                }}
              >{data.primary_type_label}</span>
            )}
            {data.short_address && <span>{data.short_address}</span>}
            {rating && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: T.brass }}>★</span>
                <strong style={{ color: T.ink }}>{rating.toFixed(1)}</strong>
                {ratingCount ? <span style={{ color: T.ink3 }}>({ratingCount.toLocaleString()} reviews)</span> : null}
              </span>
            )}
            {openNow != null && (
              <span
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: openNow ? "#e6f3e9" : "#fbe2e2",
                  color: openNow ? "#1e6b32" : "#922222",
                  padding: "2px 10px", borderRadius: 999,
                  fontSize: 11, fontWeight: 600,
                }}
              >
                <span style={{
                  width: 6, height: 6, borderRadius: 999,
                  background: openNow ? "#2f8a52" : "#c14545",
                }} />
                {openNow ? "Open now" : "Closed"}
              </span>
            )}
            {priceTier && (
              <span
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 11, fontWeight: 600, color: T.brass,
                  letterSpacing: "0.10em",
                }}
              >{priceTier}</span>
            )}
          </div>

          {/* Amenity chip row · auto-emitted from Google's amenity-ish
              fields. Only chips Google explicitly confirms TRUE. */}
          {data.amenities && data.amenities.length > 0 && (
            <div
              data-testid="stay-drawer-amenities"
              style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
            >
              {data.amenities.map((a) => (
                <span
                  key={a.code}
                  data-testid={`stay-drawer-amenity-${a.code}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: `${T.brass}14`, color: T.ink,
                    border: `1px solid ${T.brass}33`,
                    borderRadius: 999,
                    padding: "5px 11px",
                    fontSize: 11.5, fontWeight: 500,
                  }}
                >
                  <span aria-hidden="true">{a.icon}</span>
                  {a.label}
                </span>
              ))}
            </div>
          )}

          {/* Editorial summary */}
          {data.editorial_summary && (
            <p
              data-testid="stay-drawer-summary"
              style={{ fontSize: 14.5, lineHeight: 1.55, color: T.ink2, margin: 0 }}
            >
              {data.editorial_summary}
            </p>
          )}

          <DrawerVoiceLine text={voiceLine} T={T} testid="stay-drawer-concierge-voice" />

          <StaticMapBand lat={data.lat} lng={data.lng} label="On the map" testid="stay-drawer-map" />

          {/* Photo gallery thumbnails */}
          {photos.length > 1 && (
            <div
              data-testid="stay-drawer-gallery"
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(photos.length, 4)}, 1fr)`,
                gap: 6,
              }}
            >
              {photos.slice(0, 4).map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActivePhoto(i)}
                  data-testid={`stay-drawer-gallery-${i}`}
                  style={{
                    aspectRatio: "4 / 3",
                    background: `url(${p}) center/cover no-repeat`,
                    border: i === activePhoto ? `2px solid ${T.brass}` : `1px solid ${T.line}`,
                    borderRadius: 8, cursor: "pointer", padding: 0,
                  }}
                  aria-label={`Photo ${i + 1}`}
                />
              ))}
            </div>
          )}

          {/* Nearby landmarks · auto-pulled from Google's addressDescriptor */}
          {data.nearby_landmarks && data.nearby_landmarks.length > 0 && (
            <div data-testid="stay-drawer-landmarks" style={{ fontSize: 12.5, color: T.ink2 }}>
              <span
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 10, color: T.ink3,
                  letterSpacing: "0.16em", textTransform: "uppercase",
                  fontWeight: 600, marginRight: 8,
                }}
              >Nearby</span>
              {data.nearby_landmarks.map((l, i) => (
                <span key={i} style={{ marginRight: 12 }}>
                  <strong style={{ color: T.ink }}>{l.name}</strong>
                  {l.distance_m ? <span style={{ color: T.ink3 }}> · {Math.round(l.distance_m)}m</span> : null}
                </span>
              ))}
            </div>
          )}

          {/* Tables within reach · cross-pillar Concierge® moment
             (Google APIs leverage roadmap #1, 29 Jun 2026). Walking
             + driving minutes are LIVE — driving uses current-traffic
             data. Each chip deep-links into /table pre-filled with
             the restaurant's name so the brief is one click away. */}
          {!tablesLoading && tables && tables.length > 0 && (
            <div data-testid="stay-drawer-tables-nearby" style={{ marginTop: 6 }}>
              <h3
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 11, color: T.brass,
                  letterSpacing: "0.18em", textTransform: "uppercase",
                  fontWeight: 700, margin: "0 0 10px",
                }}
              >Tables within reach</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {tables.slice(0, 6).map((r) => (
                  <a
                    key={r.place_id}
                    data-testid={`stay-table-chip-${r.place_id}`}
                    href={`/table?q=${encodeURIComponent(r.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "10px 14px",
                      background: T.card,
                      border: `1px solid ${T.line2}`,
                      borderRadius: 12,
                      textDecoration: "none",
                      color: T.ink,
                      transition: "all 120ms ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = T.brass2;
                      e.currentTarget.style.background = T.paper2;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = T.line2;
                      e.currentTarget.style.background = T.card;
                    }}
                  >
                    <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.name}
                      </span>
                      <span style={{ fontSize: 11.5, color: T.ink3 }}>
                        ★ {r.rating}
                        {r.user_ratings_total ? ` · ${r.user_ratings_total.toLocaleString()} ratings` : ""}
                        {r.cuisine_hint ? ` · ${r.cuisine_hint}` : ""}
                      </span>
                    </span>
                    <span
                      className="t-mono"
                      style={{
                        fontSize: 11, color: T.ink2,
                        letterSpacing: "0.04em",
                        whiteSpace: "nowrap",
                        textAlign: "right",
                      }}
                    >
                      {r.walk_min != null ? `${r.walk_min}min walk` : ""}
                      {r.walk_min != null && r.drive_min != null ? "  ·  " : ""}
                      {r.drive_min != null ? `${r.drive_min}min cab` : ""}
                    </span>
                  </a>
                ))}
              </div>
              <p style={{
                fontSize: 10.5, color: T.ink3, marginTop: 8,
                fontStyle: "italic",
              }}>
                Click any to send a brief — your concierge holds the table.
              </p>
            </div>
          )}

          {/* What's inside · Google subDestinations (spas / restaurants / clubs within the hotel) */}
          {data.sub_destinations && data.sub_destinations.length > 0 && (
            <div data-testid="stay-drawer-subdest" style={{ marginTop: 4 }}>
              <h3
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 11, color: T.brass,
                  letterSpacing: "0.18em", textTransform: "uppercase",
                  fontWeight: 700, margin: "0 0 8px",
                }}
              >What's inside this property</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {data.sub_destinations.map((s) => (
                  <span
                    key={s.place_id}
                    data-testid="stay-drawer-subdest-item"
                    style={{
                      background: T.bg,
                      border: `1px solid ${T.line2}`,
                      borderRadius: 999,
                      padding: "5px 11px",
                      fontSize: 12, color: T.ink,
                      fontWeight: 500,
                    }}
                  >{s.name}</span>
                ))}
              </div>
            </div>
          )}

          {/* Local context · neighborhood + timezone + plus code (concierge-shareable) */}
          {(data.time_zone || data.plus_code || data.neighborhood) && (
            <div
              data-testid="stay-drawer-context"
              style={{
                display: "flex", flexWrap: "wrap", gap: 14,
                fontSize: 11.5, color: T.ink3,
                paddingTop: 4,
              }}
            >
              {data.neighborhood && (
                <span>📍 <strong style={{ color: T.ink2 }}>{data.neighborhood}</strong></span>
              )}
              {data.time_zone && (
                <span>🕐 <strong style={{ color: T.ink2 }}>{data.time_zone.replace("_", " ")}</strong>{data.utc_offset_min != null ? <> · UTC{data.utc_offset_min >= 0 ? "+" : ""}{Math.round(data.utc_offset_min / 60)}</> : null}</span>
              )}
              {data.plus_code && (
                <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 10.5 }}>
                  · <strong style={{ color: T.ink2 }}>{data.plus_code}</strong>
                </span>
              )}
            </div>
          )}

          {/* Secondary hours · kitchen / brunch / breakfast service windows */}
          {data.secondary_hours && data.secondary_hours.length > 0 && (
            <div data-testid="stay-drawer-secondary-hours" style={{ fontSize: 12, color: T.ink2 }}>
              {data.secondary_hours.map((sh, i) => (
                <details key={i} style={{ marginBottom: 6 }}>
                  <summary style={{
                    cursor: "pointer", fontWeight: 600, color: T.ink,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 11, letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}>
                    {sh.type.replace(/_/g, " ").toLowerCase()} hours
                  </summary>
                  <div style={{ marginTop: 6, paddingLeft: 4, whiteSpace: "pre-line" }}>
                    {sh.weekday_hours.slice(0, 7).join("\n")}
                  </div>
                </details>
              ))}
            </div>
          )}

          {/* Mini-map · removed 30 Jun 2026 (founder ruling).
              The Static Map band at the top of the drawer (G2 · shared
              `<StaticMapBand/>`) replaces this redundant golden tile.
              The "Directions" deeplink below still opens Google Maps. */}

          {/* Quick deeplink chips · directions / photos / reviews on Google */}
          {(data.directions_url || data.photos_url || data.reviews_url) && (
            <div data-testid="stay-drawer-deeplinks" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {data.directions_url && (
                <a
                  href={data.directions_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="stay-drawer-directions"
                  style={smallChipStyle}
                >Directions ↗</a>
              )}
              {data.photos_url && (
                <a
                  href={data.photos_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="stay-drawer-photos-link"
                  style={smallChipStyle}
                >More photos ↗</a>
              )}
              {data.reviews_url && (
                <a
                  href={data.reviews_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="stay-drawer-reviews-link"
                  style={smallChipStyle}
                >All reviews ↗</a>
              )}
            </div>
          )}

          {/* Meta rows · address · phone · website · hours */}
          <div data-testid="stay-drawer-meta" style={{ display: "grid", gap: 6, fontSize: 13 }}>
            {data.address_line && (
              <DrawerMetaRow label="Address" value={data.address_line} />
            )}
            {data.phone && (
              <DrawerMetaRow
                label="Phone"
                value={<a href={`tel:${data.phone}`} style={{ color: T.ink, textDecoration: "underline" }}>{data.phone}</a>}
              />
            )}
            {data.website && (
              <DrawerMetaRow
                label="Website"
                value={
                  <a href={data.website} target="_blank" rel="noreferrer noopener" style={{ color: T.brass, textDecoration: "underline" }}>
                    {(data.website || "").replace(/^https?:\/\//, "").replace(/\/.*$/, "")}
                  </a>
                }
              />
            )}
            {data.weekday_hours && data.weekday_hours.length > 0 && (
              <DrawerMetaRow
                label="Hours"
                value={
                  <span style={{ whiteSpace: "pre-line", color: T.ink2 }}>
                    {data.weekday_hours.slice(0, 7).join("\n")}
                  </span>
                }
              />
            )}
            {isHotel && (
              <DrawerMetaRow label="Type" value="Hotel · Lodging" />
            )}
          </div>

          {loadingDetail && (
            <p style={{ fontSize: 11.5, color: T.ink3, fontStyle: "italic", margin: 0 }}>
              Loading the full atmosphere…
            </p>
          )}

          {/* What guests say · top 3 real Google reviews · rendered
             via shared <DrawerReviewsBand> for parity with the rest
             of the pillars (Parity Sprint Phase 2, 30 Jun 2026). */}
          <DrawerReviewsBand reviews={data.reviews} limit={3} T={T} testid="stay-drawer-reviews" />

          {/* CTAs · Concierge® primary · direct reserve secondary · founder ruling 30 Jun 2026 */}
          <div data-testid="stay-drawer-ctas" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
            <button
              type="button"
              onClick={onRequest}
              data-testid="stay-drawer-request"
              style={{
                background: T.ink, color: T.card,
                border: 0, borderRadius: 999,
                padding: "14px 22px",
                fontSize: 14, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
                textAlign: "center",
              }}
            >
              Request via Concierge®
            </button>
            {data.google_hotel_url && (
              <a
                href={data.google_hotel_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="stay-drawer-book"
                style={{
                  background: "transparent", color: T.brass,
                  border: `1.5px solid ${T.brass}`, borderRadius: 999,
                  padding: "12px 22px",
                  fontSize: 13.5, fontWeight: 500,
                  textAlign: "center", textDecoration: "none",
                  fontFamily: "inherit", display: "block",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
              >
                Reserve ↗
              </a>
            )}
          </div>

          <p
            style={{
              fontSize: 11.5, color: T.ink3, fontStyle: "italic",
              textAlign: "center", margin: 0, lineHeight: 1.5,
            }}
          >
            Ask your Concierge® team to arrange it · we hold the door open.
          </p>
        </div>
      </aside>
    </div>
  );
}

function DrawerMetaRow({ label, value }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 12, alignItems: "start", paddingTop: 4 }}>
      <span style={{
        fontFamily: "ui-monospace, monospace",
        fontSize: 10, color: T.ink3,
        letterSpacing: "0.16em", textTransform: "uppercase",
        fontWeight: 600,
      }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

const chromeBtnStyle = {
  background: "transparent",
  border: `1px solid ${T.line2}`,
  borderRadius: 999,
  padding: "5px 12px",
  fontSize: 10.5, letterSpacing: "0.16em",
  color: T.ink2, cursor: "pointer",
  fontFamily: "ui-monospace, monospace",
  fontWeight: 600, textTransform: "uppercase",
};

const smallChipStyle = {
  background: "transparent",
  border: `1px solid ${T.line2}`,
  borderRadius: 999,
  padding: "5px 11px",
  fontSize: 11.5, color: T.ink2,
  textDecoration: "none",
  fontFamily: "inherit", fontWeight: 500,
};


/* ── Smart filter bar + filtered/sorted grid ──────────────────────
 * Filters apply CLIENT-SIDE on the already-loaded cards · no extra
 * Google calls. The filter bar only shows chips for values that
 * actually exist in the loaded set (e.g. no "$$$$" chip when no
 * very-expensive hotel was returned). */

const _LODGING_TYPE_LABEL = {
  hotel:              "Hotel",
  resort_hotel:       "Resort",
  bed_and_breakfast:  "B&B",
  hostel:             "Hostel",
  motel:              "Motel",
  guest_house:        "Guest house",
  spa:                "Spa retreat",
  campground:         "Campground",
  cottage:            "Cottage",
};

const _PRICE_TIER_BY_LEVEL = {
  PRICE_LEVEL_INEXPENSIVE:    "$",
  PRICE_LEVEL_MODERATE:       "$$",
  PRICE_LEVEL_EXPENSIVE:      "$$$",
  PRICE_LEVEL_VERY_EXPENSIVE: "$$$$",
};

function StayFilterBar({
  cards, filterPrice, setFilterPrice,
  filterMinRating, setFilterMinRating,
  filterType, setFilterType,
  sortBy, setSortBy,
}) {
  // Compute which chip values actually appear in the loaded data.
  const availablePrices = useMemo(() => {
    const s = new Set();
    cards.forEach((c) => { if (c.price_level && _PRICE_TIER_BY_LEVEL[c.price_level]) s.add(_PRICE_TIER_BY_LEVEL[c.price_level]); });
    return ["$", "$$", "$$$", "$$$$"].filter((p) => s.has(p));
  }, [cards]);

  const availableTypes = useMemo(() => {
    const s = new Set();
    cards.forEach((c) => (c.place_types || []).forEach((t) => { if (_LODGING_TYPE_LABEL[t]) s.add(t); }));
    return Array.from(s).sort((a, b) => _LODGING_TYPE_LABEL[a].localeCompare(_LODGING_TYPE_LABEL[b]));
  }, [cards]);

  return (
    <div
      data-testid="stay-filter-bar"
      style={{
        background: T.card,
        border: `1px solid ${T.line}`,
        borderRadius: 14,
        padding: "12px 16px",
        margin: "8px 0 22px",
        display: "flex", flexDirection: "column", gap: 10,
        boxShadow: "0 2px 8px rgba(33,28,21,0.03)",
      }}
    >
      {availablePrices.length > 0 && (
        <FilterRow
          label="Price"
          testid="stay-filter-price"
        >
          <Chip on={filterPrice === "any"} onClick={() => setFilterPrice("any")} testid="stay-filter-price-any">Any</Chip>
          {availablePrices.map((p) => (
            <Chip key={p} on={filterPrice === p} onClick={() => setFilterPrice(p)} testid={`stay-filter-price-${p}`}>{p}</Chip>
          ))}
        </FilterRow>
      )}
      <FilterRow label="Rating" testid="stay-filter-rating">
        <Chip on={filterMinRating === 0}   onClick={() => setFilterMinRating(0)}   testid="stay-filter-rating-any">Any</Chip>
        <Chip on={filterMinRating === 3}   onClick={() => setFilterMinRating(3)}   testid="stay-filter-rating-3">★3+</Chip>
        <Chip on={filterMinRating === 4}   onClick={() => setFilterMinRating(4)}   testid="stay-filter-rating-4">★4+</Chip>
        <Chip on={filterMinRating === 4.5} onClick={() => setFilterMinRating(4.5)} testid="stay-filter-rating-45">★4.5+</Chip>
      </FilterRow>
      {availableTypes.length > 1 && (
        <FilterRow label="Type" testid="stay-filter-type">
          <Chip on={filterType === "any"} onClick={() => setFilterType("any")} testid="stay-filter-type-any">Any</Chip>
          {availableTypes.map((t) => (
            <Chip key={t} on={filterType === t} onClick={() => setFilterType(t)} testid={`stay-filter-type-${t}`}>
              {_LODGING_TYPE_LABEL[t]}
            </Chip>
          ))}
        </FilterRow>
      )}
      <FilterRow label="Sort by" testid="stay-filter-sort">
        <Chip on={sortBy === "rating"}     onClick={() => setSortBy("rating")}     testid="stay-sort-rating">Top rated</Chip>
        <Chip on={sortBy === "popularity"} onClick={() => setSortBy("popularity")} testid="stay-sort-pop">Most reviewed</Chip>
        <Chip on={sortBy === "price_asc"}  onClick={() => setSortBy("price_asc")}  testid="stay-sort-pasc">Price ↑</Chip>
        <Chip on={sortBy === "price_desc"} onClick={() => setSortBy("price_desc")} testid="stay-sort-pdesc">Price ↓</Chip>
        <Chip on={sortBy === "name"}       onClick={() => setSortBy("name")}       testid="stay-sort-name">A → Z</Chip>
      </FilterRow>
    </div>
  );
}

function FilterRow({ label, testid, children }) {
  return (
    <div
      data-testid={testid}
      style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
    >
      <span
        style={{
          fontFamily: "ui-monospace, monospace",
          fontSize: 10.5, color: T.ink3,
          letterSpacing: "0.16em", textTransform: "uppercase",
          fontWeight: 600, minWidth: 56,
        }}
      >{label}</span>
      {children}
    </div>
  );
}

function Chip({ on, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        background: on ? T.ink : "transparent",
        color: on ? T.bg : T.ink2,
        border: `1px solid ${on ? T.ink : T.line2}`,
        borderRadius: 999,
        padding: "5px 12px",
        fontSize: 12, fontWeight: 500,
        cursor: "pointer", fontFamily: "inherit",
        transition: "all 0.12s",
      }}
    >{children}</button>
  );
}

const _PRICE_RANK = {
  PRICE_LEVEL_INEXPENSIVE:    1,
  PRICE_LEVEL_MODERATE:       2,
  PRICE_LEVEL_EXPENSIVE:      3,
  PRICE_LEVEL_VERY_EXPENSIVE: 4,
};

function FilteredResultsGrid({ cards, filterPrice, filterMinRating, filterType, sortBy, onOpen, onBrief }) {
  const list = useMemo(() => {
    let out = (cards || []).slice();
    if (filterPrice !== "any") {
      out = out.filter((c) => _PRICE_TIER_BY_LEVEL[c.price_level] === filterPrice);
    }
    if (filterMinRating > 0) {
      out = out.filter((c) => (c.rating || 0) >= filterMinRating);
    }
    if (filterType !== "any") {
      out = out.filter((c) => (c.place_types || []).includes(filterType));
    }
    out.sort((a, b) => {
      if (sortBy === "rating") {
        return (b.rating || 0) - (a.rating || 0);
      }
      if (sortBy === "popularity") {
        return (b.user_ratings_total || 0) - (a.user_ratings_total || 0);
      }
      if (sortBy === "price_asc") {
        // Live rates win over the coarse $-tier when both cards have one.
        if (a.price_per_night != null && b.price_per_night != null) return a.price_per_night - b.price_per_night;
        return (_PRICE_RANK[a.price_level] || 99) - (_PRICE_RANK[b.price_level] || 99);
      }
      if (sortBy === "price_desc") {
        if (a.price_per_night != null && b.price_per_night != null) return b.price_per_night - a.price_per_night;
        return (_PRICE_RANK[b.price_level] || 0) - (_PRICE_RANK[a.price_level] || 0);
      }
      if (sortBy === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      return 0;
    });
    return out;
  }, [cards, filterPrice, filterMinRating, filterType, sortBy]);

  if (list.length === 0) {
    return (
      <div
        data-testid="stay-filtered-empty"
        style={{
          padding: 36, textAlign: "center", color: T.ink3,
          fontSize: 13.5, fontStyle: "italic",
          border: `1px dashed ${T.line2}`, borderRadius: 14,
        }}
      >
        No stays match those filters. Loosen them above.
      </div>
    );
  }

  return (
    <div
      data-testid="stay-results"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 18,
      }}
    >
      {list.map((h, i) => (
        <PillarCard
          key={h.hotel_id || h.place_id || i}
          venue={{
            name: h.name,
            photo_url: h.photo_url,
            short_address: h.short_address || h.city_name,
            address_line: h.address_line,
            city_name: h.city_name,
            rating: h.rating,
            user_ratings_total: h.user_ratings_total,
          }}
          testidPrefix="stay"
          onView={() => onOpen(h)}
          onBrief={() => onBrief?.(h)}
          briefLabel="Send a brief"
          topLeftPill={h.hotel_class ? (
            <span style={{
              background: "rgba(252,250,244,0.92)", color: T.brass,
              padding: "3px 9px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.14em",
              fontWeight: 600, textTransform: "uppercase",
              border: `1px solid ${T.brass}44`,
            }}>{h.hotel_class}</span>
          ) : null}
          topRightPill={h.price_per_night != null ? (
            /* Live nightly rate · hotels engine · display-only, desk
               books. Cites the underlying rate source on hover. */
            <span
              data-testid="stay-card-live-rate"
              title={h.price_source ? `Rate via ${h.price_source} · for your dates` : "Live rate for your dates"}
              style={{
                background: "rgba(33,28,21,0.82)", color: "#FCFAF4",
                padding: "4px 10px", borderRadius: 999,
                fontFamily: "ui-monospace, monospace",
                fontSize: 10.5, letterSpacing: "0.06em", fontWeight: 700,
              }}
            >
              from {new Intl.NumberFormat(undefined, { style: "currency", currency: h.price_currency || "USD", maximumFractionDigits: 0 }).format(h.price_per_night)}/nt
            </span>
          ) : null}
        />
      ))}
    </div>
  );
}

/* ── BrandedVenueBanner · top-of-results banner shown when the user
 *    types a brand name that Google has indexed as a non-hotel venue
 *    (wellness, spa, health, etc.). We don't suppress lodging — we
 *    keep area hotels below — but the member always sees the property
 *    they typed AND a direct path to ask the Concierge® for a stay. */
function BrandedVenueBanner({ hint, onRequest }) {
  const typeLabel = (hint.types || [])
    .filter((t) => !["point_of_interest", "establishment"].includes(t))
    .map((t) => t.replace(/_/g, " "))
    .slice(0, 2).join(" · ");
  return (
    <div
      data-testid="stay-branded-venue-banner"
      style={{
        background: `linear-gradient(135deg, ${T.bg} 0%, ${T.brass}11 100%)`,
        border: `1px solid ${T.brass}55`,
        borderRadius: 16,
        padding: "18px 22px",
        margin: "8px 0 22px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 14, flexWrap: "wrap",
      }}
    >
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{
          fontFamily: "ui-monospace, monospace",
          fontSize: 10.5, letterSpacing: "0.22em",
          color: T.brass, fontWeight: 700, textTransform: "uppercase",
          marginBottom: 6,
        }}>✦ Branded venue · Concierge®-verified</div>
        <h3
          className="t-serif"
          style={{ fontSize: 20, fontWeight: 500, margin: "0 0 4px", color: T.ink }}
          data-testid="stay-branded-venue-name"
        >
          {hint.name}
        </h3>
        <p style={{ fontSize: 12.5, color: T.ink3, margin: 0, lineHeight: 1.5 }}>
          {hint.short_address || hint.address_line || ""}
          {typeLabel ? <> · <em>{typeLabel}</em></> : null}
        </p>
        <p style={{ fontSize: 12, color: T.ink3, margin: "8px 0 0", fontStyle: "italic" }}>
          Google has it indexed as a venue, not a hotel — the Concierge® team can still arrange your stay here.
        </p>
      </div>
      <button
        type="button"
        onClick={onRequest}
        data-testid="stay-branded-venue-request"
        style={{
          background: T.ink, color: T.card, border: 0, borderRadius: 999,
          padding: "12px 22px", fontSize: 13.5, fontWeight: 600,
          cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
        }}
      >
        Request stay via Concierge® →
      </button>
    </div>
  );
}

